/**
 * pathDisc — the overlays a circular path disc wears to say its state, shared
 * by the two surfaces that draw one: the Paths hero orbit (`PathOrbit`) and the
 * path nav rows beneath it (`progressHero.renderPathRow`). One renderer, so a
 * row's tile and its orbit disc can never disagree about what "in flight" or
 * "done" looks like.
 *
 * State is carried by treatment, never by a different image:
 *   - a progress ring around a path that is genuinely part-way (0 and 100 draw
 *     nothing — 0 has nothing to show, 100 is already said by the check);
 *   - a check badge on the rim of a finished path;
 *   - a padlock badge on the rim of a path the viewer cannot work yet
 *     (Premium, or phase-locked).
 *
 * The host positions the overlays: both `.path-disc__progress` and
 * `.path-disc__badge` are `position: absolute` against the disc, sized in
 * percentages of it, so the same markup scales from a 40px row tile to the
 * orbit's centre disc (`css/components/path-orbit.css`, §path-disc).
 *
 * @module components/pathDisc
 */

// Ring geometry, in the ring SVG's own 100x100 viewBox. r=46 with a 5-unit
// stroke keeps the whole stroke band inside the box (46 + 2.5 = 48.5); the
// host sets the actual stroke width via `--disc-ring-width`.
export const PROGRESS_R = 46;
export const PROGRESS_C = 2 * Math.PI * PROGRESS_R;

/**
 * Badge glyphs, keyed by badge kind. Each `<svg>` fills the badge circle
 * exactly (CSS: `inset: 0`) and places its symbol at an explicit inset INSIDE
 * the 16-unit viewBox, so the glyph is centred by vector arithmetic on the
 * same box the circle paints. Centring it with CSS instead (a percent-sized
 * svg in a percent-sized grid cell) let the two round to pixels separately,
 * which sat every glyph a hair up and to the right at badge sizes.
 *
 * The check symbol is 4:3 (its native box), so it gets a 10×7.5 frame; the
 * padlock is `#icon-lock-badge`, drawn square for this size — two primitives
 * — not the detailed privacy-mode lock, which turns to mush under ~20px.
 * @type {Readonly<Record<string, string>>}
 */
const GLYPHS = Object.freeze({
  check: '<svg viewBox="0 0 16 16" aria-hidden="true"><use href="#icon-checkmark" x="3" y="4.25" width="10" height="7.5"/></svg>',
  lock: '<svg viewBox="0 0 16 16" aria-hidden="true"><use href="#icon-lock-badge" x="3" y="3" width="10" height="10"/></svg>'
});

/**
 * Clamp a percentage to [0, 100], coercing non-finite input to 0.
 * @param {number} v - Raw value
 * @returns {number} Clamped 0-100 value
 */
export function clampPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/**
 * The overlay markup for one disc: a progress ring when `percent` is strictly
 * between 0 and 100, and/or a rim badge. Returns '' when there is nothing to
 * overlay, so callers can append it unconditionally.
 *
 * The ring's drawn length is written twice on purpose: as the dasharray that
 * paints it, and as `--disc-ring-drawn` so the orbit's entrance keyframe can
 * sweep the ring in from nothing.
 *
 * @param {Object} [opts]
 * @param {number|null} [opts.percent=null] - Completion percent, or null for no ring
 * @param {'check'|'lock'|null} [opts.badge=null] - Rim badge kind, or null for none
 * @returns {string} HTML string
 */
export function discOverlaysHtml({ percent = null, badge = null } = {}) {
  const pct = percent == null ? null : clampPct(percent);
  let ring = '';
  if (pct != null && pct > 0 && pct < 100) {
    const drawn = ((pct / 100) * PROGRESS_C).toFixed(2);
    // -90 about the circle's own centre starts the sweep at twelve o'clock.
    ring = `<svg class="path-disc__progress" viewBox="0 0 100 100" aria-hidden="true">`
      + `<circle class="path-disc__progress-track" cx="50" cy="50" r="${PROGRESS_R}" fill="none"/>`
      + `<circle class="path-disc__progress-fill" cx="50" cy="50" r="${PROGRESS_R}" fill="none"`
      + ` transform="rotate(-90 50 50)" stroke-dasharray="${drawn} ${PROGRESS_C.toFixed(2)}"`
      + ` style="--disc-ring-drawn:${drawn}"/>`
      + '</svg>';
  }

  const glyph = badge ? GLYPHS[badge] : null;
  const badgeHtml = glyph
    ? `<span class="path-disc__badge path-disc__badge--${badge}">${glyph}</span>`
    : '';

  return ring + badgeHtml;
}
